"use client";

import Block from "@/components/elements/Block";
import Container from "@/components/elements/Container";
import Heading2 from "@/components/elements/Heading2";
import { jsonToHtml } from "@contentstack/json-rte-serializer";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildContentstackImageUrl } from "@/lib/utils";

// BL-930: same pair ProductCarousel uses (BL-275). The cards ahead of the fold
// stay lazy; the ones a shopper sees first are fetched up front, because a card
// whose photo has not arrived reads as an empty card — which is what the report
// shows on an iPhone, where lazy images inside a horizontal scroller are not
// reliably triggered by scrolling sideways.
const IMG_LAZY = { fetchPriority: "low", loading: "lazy" };
const IMG_EAGER = { fetchPriority: "auto", loading: "eager" };
const EAGER_CARDS = 4;

const InstagramIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const PostCard = ({ post, eager = false }) => (
  <div className="flex-none w-64 bg-white rounded-2xl overflow-hidden shadow-sm">
    <div className="flex items-center justify-between px-3 py-3">
      <div className="flex items-center gap-2">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-none"
          style={{ backgroundColor: post.avatarColor }}
        >
          <Image
            src={buildContentstackImageUrl(post?.profile_image?.url, 96)}
            alt={post?.profile_image?.description || post?.profile_image?.title || ''}
            width={96}
            height={96}
            sizes="36px"
            className="rounded-full"
          />
        </div>
        <div className="leading-tight">
          <p className="font-semibold text-sm text-gray-900">
            {post.profile_name}
          </p>
          {/* Brand green, which is what production uses here. The grey it
              replaced sat at 2.6:1 against the white card (BL-471). */}
          <p className="text-xs text-primary">{post.profile_handle}</p>
        </div>
      </div>
      <span className="text-gray-400">
        <InstagramIcon />
      </span>
    </div>
    <div className="w-full aspect-square bg-stone-200">
      <Image
        // Card is w-64 (256px); request 2x for retina screens.
        src={buildContentstackImageUrl(post?.instagram_image?.url, 512)}
        alt={post?.instagram_image?.description || post?.instagram_image?.title || ''}
        width={368}
        height={368}
        sizes="368px"
        className="object-cover w-full h-full"
        {...(eager ? IMG_EAGER : IMG_LAZY)}
      />
    </div>
  </div>
);

const InstagramFeed = ({ block, position }) => {
  const { cards, content, heading, settings } = block;
  const scrollRef = useRef(null);

  // BL-930: the arrows moved the strip but knew nothing about it, so at the end
  // of the row they stayed live and clicking did nothing at all. The native
  // scroll clamps itself; what was missing was telling the shopper they had run
  // out of posts.
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // A sub-pixel gap is normal at the end of a scroll, hence the 1px slack.
    const maxScroll = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(maxScroll <= 1 || el.scrollLeft >= maxScroll - 1);
  }, []);

  useEffect(() => {
    syncEdges();
    window.addEventListener("resize", syncEdges);
    return () => window.removeEventListener("resize", syncEdges);
  }, [syncEdges]);

  const scroll = (direction) => {
    scrollRef.current?.scrollBy({ left: direction * 280, behavior: "smooth" });
  };

  const arrowClassName = (disabled) =>
    `absolute top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow flex items-center justify-center text-gray-600 transition-colors ${
      disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50"
    }`;

  function isEffectivelyUndefined(value) {
    if (value == null) return true;

    const normalized = value.trim().toLowerCase();

    // Remove HTML tags
    const textOnly = normalized.replace(/<[^>]*>/g, "").trim();

    return textOnly === "" || textOnly === "&nbsp;";
  }

  const contentHtml = content ? jsonToHtml(content) : null;

  const descriptionText = isEffectivelyUndefined(contentHtml)
    ? null
    : contentHtml;

  return (
    <Block name="instagram_feed" settings={settings} position={position}>
      {heading && (
        <Heading2
          color={settings.text_color?.color_selector || "text-white"}
          className="text-4xl text-center mb-8"
        >
          {heading}
        </Heading2>
      )}
      {descriptionText && (
        <div
          className={`text-base max-w-xl mx-auto mb-10 text-center ${settings.text_color?.color_selector || "text-white"}`}
          dangerouslySetInnerHTML={{ __html: descriptionText }}
        />
      )}
      <Container>
        <div className="relative">
          <button
            type="button"
            onClick={() => scroll(-1)}
            disabled={atStart}
            className={`left-4 ${arrowClassName(atStart)}`}
            aria-label="Scroll left"
          >
            ‹
          </button>

          {/* BL-473: the carousel scrolls but holds no focusable content — the
              cards are images, and the arrows sit outside it — so a keyboard
              user had no way to reach or move it at all. tabIndex makes it
              focusable and arrow-key scrollable; role and label make it
              announce as something rather than an unnamed group.

              The focus ring is not decoration here: making a div focusable
              without a visible indicator trades a 2.1.1 failure for a 2.4.7
              one. */}
          <div
            ref={scrollRef}
            onScroll={syncEdges}
            tabIndex={0}
            role="region"
            aria-label={heading || "Instagram posts"}
            className="flex gap-4 overflow-x-auto px-16 pb-2 scroll-smooth focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sea"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {cards.map((post, index) => (
              <PostCard key={index} post={post} eager={index < EAGER_CARDS} />
            ))}
          </div>

          <button
            type="button"
            onClick={() => scroll(1)}
            disabled={atEnd}
            className={`right-4 ${arrowClassName(atEnd)}`}
            aria-label="Scroll right"
          >
            ›
          </button>
        </div>
      </Container>
    </Block>
  );
};

export default InstagramFeed;
